import React from "react";
import {Form, Select} from "antd";

import InputWrap from "../../InputWrap";
import SensorScalingFields from "./SensorScalingFields";
import {SensorSettings, SettingsSensor, Unit} from "../../../store/branches/sensors/stateTypes";

import classes from "../EditSensorsTypes.module.scss";

const {Option} = Select;

interface UnifiedInputFormProps {
    pulseGroup: SensorSettings
    cliGroup: SensorSettings
    units?: Unit[]
    form: any
}

// Input 9/10 are the same 2 physical wires as the "Pulse" (group 2000) and "CLI"
// (group 3000) sensors — a DIP switch on the device selects which one is live, never
// both. This form unifies them into one mode select per input instead of showing both
// groups unconditionally. "Contact" has no hardware signal at all (see CLAUDE.md-level
// notes from the stakeholder feedback) — it's purely a user declaration, persisted as
// type: "CONTACT" on the CLI sensor's record, since CLI's sensor id is the one that
// exists for every IE-Node device type and so can safely double as the durable
// per-input "declared mode" slot without a backend schema change.
const hasConfig = (sensor?: SettingsSensor) =>
    sensor?.unit != null || sensor?.k_value != null || sensor?.raw1 != null;

const UnifiedInputForm: React.FC<UnifiedInputFormProps> = ({pulseGroup, cliGroup, units, form}) => {
    return (
        <React.Fragment>
            <div className={classes.driver}>
                <span>Sensor 9 / Sensor 10</span>
                <div className={classes.cssBaseLine}/>
            </div>

            <div className={classes.blocksWrap}>
                {
                    cliGroup?.sensors?.map((cliSensor: SettingsSensor, index: number) => {
                        const pulseSensor = pulseGroup?.sensors?.[index];
                        const inputLabel = `Sensor ${9 + index}`;

                        const initialMode = cliSensor?.type === "CONTACT"
                            ? "Contact"
                            : (hasConfig(cliSensor) || !hasConfig(pulseSensor) ? "CLI" : "Pulse");

                        return <div className={classes.sideBlock} key={`unified_input_${cliSensor.id}${index}`}>
                            <InputWrap title={`${inputLabel} Mode`}
                                       className={classes.wideInput}
                                       key={`input_mode_${cliSensor.id}${index}`}>
                                <Form.Item name={`${cliSensor.id} inputMode`}
                                           initialValue={initialMode}
                                >
                                    <Select>
                                        <Option value="CLI">CLI (4-20mA)</Option>
                                        <Option value="Pulse">Pulse</Option>
                                        <Option value="Contact">Contact</Option>
                                    </Select>
                                </Form.Item>
                            </InputWrap>

                            <Form.Item noStyle
                                       shouldUpdate={(prevValues, curValues) =>
                                           prevValues[`${cliSensor.id} inputMode`] !== curValues[`${cliSensor.id} inputMode`]
                                       }
                            >
                                {() => {
                                    const mode = form.getFieldValue(`${cliSensor.id} inputMode`) ?? initialMode;

                                    if (mode === "Contact") {
                                        return <InputWrap title={`${inputLabel} Scaling`}
                                                           className={classes.wideInput}
                                                           key={`contact_note_${cliSensor.id}${index}`}>
                                            <span className={classes.disabledNote}>
                                                No scaling required for Contact inputs.
                                            </span>
                                        </InputWrap>;
                                    }

                                    const activeSensor = mode === "Pulse" ? pulseSensor : cliSensor;
                                    if (!activeSensor) {
                                        return null;
                                    }

                                    return <SensorScalingFields sensor={activeSensor}
                                                                 units={units}
                                                                 form={form}
                                                                 titlePrefix={inputLabel}/>;
                                }}
                            </Form.Item>
                        </div>;
                    })
                }
            </div>
        </React.Fragment>
    );
};

export default UnifiedInputForm;
